import { useLocation, useNavigate } from "react-router-dom";
import "./AdAuctions.scss";
import { useEffect, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { DialogActions, Dialog, DialogTitle, DialogContent, TablePagination } from "@mui/material";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import newRequest from "../../../utils/newRequest.js";
import { ScaleLoader } from "react-spinners";
import { CustomLoadingButton } from "../../../components/ui/CustomButton.jsx";
import { MdAdd, MdArrowBackIos, MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "../../../components/confirmDialog/confirmDialog.jsx";
import InitialsAvatar from "../../../components/InitialsAvatar/InitialsAvatar.jsx";
import { trimString } from "../../../utils/genericFunctions.js";
import numeral from "numeral";

const AdAuctions = () => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const navigate = useNavigate();
    const location = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [confirmDialogAction, setConfirmDialogAction] = useState("None");
    const [confirmationActiontext, setConfirmationActiontext] = useState("");

    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");

    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("ALL"); // ALL | DRAFT | PUBLISHED | AWARDED | CLOSED | CANCELLED
    const showBack = location.state?.showBack || "N";

    const [bids, setBids] = useState([]);
    const [serverRows, setServerRows] = useState([]);
    const [serverTotal, setServerTotal] = useState(0);
    const [serverMode, setServerMode] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(4);
    const [slaNowTick, setSlaNowTick] = useState(Date.now());

    const getUrgencyMeta = (u) => {
        const v = String(u || "").toUpperCase();
        if (v === "CRITICAL") return { label: "CRITICAL", cls: "priority--critical" };
        if (v === "HIGH") return { label: "HIGH", cls: "priority--high" };
        if (v === "NORMAL") return { label: "MEDIUM", cls: "priority--normal" };
        if (v === "LOW") return { label: "LOW", cls: "priority--low" };
        return { label: v || "MEDIUM", cls: "priority--normal" };
    };

    const getStatusMeta = (s) => {
        const v = String(s || "").toUpperCase();
        if (v === "PUBLISHED") return { label: v, cls: "status--open", dot: "dot--blue" };
        if (v === "DRAFT") return { label: v, cls: "status--pending", dot: "dot--gray" };
        if (v === "AWARDED") return { label: v, cls: "status--resolved", dot: "dot--green" };
        if (v === "CLOSED") return { label: v, cls: "status--resolved", dot: "dot--green" };
        if (v === "CANCELLED") return { label: v, cls: "status--cancelled", dot: "dot--red" };
        return { label: v || "Draft", cls: "status--pending", dot: "dot--gray" };
    };

    const splitDuration = (totalSeconds = 0) => {
        const s = Math.max(0, Math.floor(totalSeconds));
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        return { d, h, m, s: sec };
    };

    const fmtRemaining = ({ d, h, m, s }, showSeconds = true) => {
        const dd = String(d).padStart(2, "0");
        const hh = String(h).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        const ss = String(s).padStart(2, "0");
        if (d > 0) return showSeconds ? `${dd}d ${hh}h ${mm}m ${ss}s` : `${dd}d ${hh}h ${mm}m`;
        return showSeconds ? `${hh}h ${mm}m ${ss}s` : `${hh}h ${mm}m`;
    };

    // SLA for bids: enabled when PUBLISHED; countdown to bid_sla.bid_closure_date
    const getBidSlaMeta = (bid, nowMs) => {
        const status = String(bid?.bid_status || "").toUpperCase();
        const enabled = status === "PUBLISHED";

        if (!enabled) {
            return { enabled: false, remainingLabel: "—", pct: 0, cls: "sla--met", breached: false, approaching: false };
        }

        const deadlineIso = bid?.bid_closure_date || bid?.bid_closure_date;
        const deadlineMs = deadlineIso ? new Date(deadlineIso).getTime() : null;

        if (!deadlineMs) {
            return { enabled: true, remainingLabel: "NO SLA", pct: 0, cls: "sla--risk", breached: false, approaching: true };
        }

        const publishIso = bid?.bid_published_at || bid?.updatedAt || bid?.createdAt;
        const startMs = publishIso ? new Date(publishIso).getTime() : null;

        const remainingSecs = Math.floor((deadlineMs - nowMs) / 1000);
        const breached = remainingSecs <= 0 || String(bid?.bid_sla_breached || "N") === "Y";
        const approaching = !breached && remainingSecs > 0 ? remainingSecs <= Math.ceil(Math.max(60, (deadlineMs - (startMs || nowMs)) / 1000) * 0.25) : false;

        const absSecs = Math.abs(remainingSecs);
        const parts = splitDuration(absSecs);

        // pct = remaining share of window
        const windowSecs = startMs ? Math.max(1, Math.floor((deadlineMs - startMs) / 1000)) : 1;
        const pct = breached ? 100 : Math.round((Math.max(0, remainingSecs) / windowSecs) * 100);

        const remainingLabel = breached ? `BREACHED ${fmtRemaining(parts, false)}` : `REMAINING ${fmtRemaining(parts, true)}`;
        const cls = breached ? "sla--breached" : approaching ? "sla--risk" : "sla--ok";

        return { enabled: true, remainingLabel, pct, cls, breached, approaching };
    };

    const countResponses = (bid) => {
        const arr = bid?.bid_participants || [];
        const total = arr.length;
        const replied = arr.filter((p) => p?.has_replied === "Y" || p?.invite_status === "REPLIED").length;
        return { total, replied, pending: Math.max(0, total - replied) };
    };

    const fmtMoney = (m) => {
        const amt = m?.amount;
        const cur = m?.currency || "";
        if (amt == null) return "—";
        try {
            return `${cur} ${Number(amt).toLocaleString()}`;
        } catch {
            return `${cur} ${amt}`;
        }
    };

    const fetchBids = async () => {
        try {
            setIsLoading(true);

            // Adjust payload keys to what your backend expects for "findAll"
            // If your controller expects empty filters, keep as {}
            const res = await newRequest.post("/auction/findAll", {
                bid_status: "",
                bid_service_type: "",
                from_date: "",
                to_date: "",
            });

            const data = res.data;
            const rows = Array.isArray(data) ? data : (data?.rows || []);
            const total = Array.isArray(data) ? rows.length : (data?.total || rows.length);

            setBids(rows);
            setServerMode(false);
            setServerRows([]);
            setServerTotal(total);
        } catch (e) {
            console.log(e);
        } finally {
            setIsLoading(false);
        }
    };

    // Local search + tab filtering (same pattern as Tickets)
    const searchFiltered = bids.filter((b) => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return (
            String(b?.bid_no || "").toLowerCase().includes(q) ||
            String(b?.bid_title || "").toLowerCase().includes(q) ||
            String(b?.bid_service_type || "").toLowerCase().includes(q) ||
            String(b?.bid_summary || "").toLowerCase().includes(q) ||
            (b?.bid_participants || []).some((p) => String(p?.ent_name || "").toLowerCase().includes(q))
        );
    });

    const tabFiltered = searchFiltered.filter((b) => {
        if (activeTab === "ALL") return true;
        return String(b?.bid_status || "").toUpperCase() === activeTab;
    });

    const effectiveRows = serverMode ? serverRows : tabFiltered;

    const paged = effectiveRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    useEffect(() => {
        const id = setInterval(() => setSlaNowTick(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        fetchBids();
    }, []);

    // Optional: if you later add /auction/search, copy the serverMode logic from Tickets.
    useEffect(() => {
        const q = searchTerm.trim();
        if (!q) {
            setServerMode(false);
            setServerRows([]);
            setServerTotal(0);
            return;
        }
        // If local results exist, don't hit server
        if (tabFiltered.length > 0) {
            setServerMode(false);
            setServerRows([]);
            setServerTotal(0);
            return;
        }
        // If no server search endpoint, do nothing
    }, [searchTerm, activeTab]);

    const handleConfirm = async () => {
        try {
            setIsProcessing(true);
            // implement actions later (publish/close/cancel)
        } catch (e) {
            console.log(e);
        } finally {
            setIsProcessing(false);
            setIsConfirmDialogOpen(false);
        }
    };

    return (
        <div>
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <motion.div
                    className="ad-auctions"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="auctions-wrapper">
                        <div className="auctions-center-wrapper">
                            <div className="auctions-center-details">
                                <div className="auctions-navbar">
                                    <div className="navbar-left">
                                        <span className="page-title">{t("auctions") || "Auctions"}</span>
                                    </div>
                                    <div className="navbar-right">
                                        <CustomLoadingButton
                                            currentTheme={currentTheme}
                                            iconPosition="end"
                                            borderRadiusType="both"
                                            variant="tint"
                                            color="#0ea5a4"
                                            tooltip={"Add new bid"}
                                            endIcon={<MdAdd />}
                                            onClick={() => navigate("/bids/new")}
                                        >
                                            {t("newBid") || "New Bid"}
                                        </CustomLoadingButton>
                                        {showBack === "Y" && <hr className="separator-v" />}
                                        {showBack === "Y" && (
                                            <span className="back-icon" onClick={() => navigate(-1)}>
                                                <MdArrowBackIos size={24} />
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="auctions-content">
                                    <div className="auctions-list">
                                        <div className="auctions-list-header">
                                            <div className="homeSearch" style={{ width: "40%" }}>
                                                <FiSearch className="homeSearchIcon" />
                                                <input
                                                    className="homeSearchInput"
                                                    type="text"
                                                    placeholder={t("search")}
                                                    value={searchTerm}
                                                    onChange={(e) => {
                                                        setSearchTerm(e.target.value);
                                                        setPage(0);
                                                    }}
                                                />
                                                {searchTerm?.length > 0 ? (
                                                    <MdClose className="homeSearchClear" onClick={() => setSearchTerm("")} />
                                                ) : (
                                                    <span className="homeSearchClearPlaceholder" />
                                                )}
                                            </div>

                                            <div className="auctions-toggle">
                                                {["ALL", "DRAFT", "PUBLISHED", "AWARDED", "CLOSED", "CANCELLED"].map((k) => (
                                                    <button
                                                        key={k}
                                                        type="button"
                                                        className={`auctions-toggle-btn ${activeTab === k ? "active" : ""}`}
                                                        onClick={() => {
                                                            setActiveTab(k);
                                                            setPage(0);
                                                        }}
                                                    >
                                                        {k === "ALL" ? (t("all") || "All") : k}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="auctions-cards">
                                            {paged.map((bid) => {
                                                const urg = getUrgencyMeta(bid?.bid_urgency);
                                                const st = getStatusMeta(bid?.bid_status);
                                                const sla = getBidSlaMeta(bid, slaNowTick);
                                                const rsp = countResponses(bid);

                                                const maxPrice = `${bid?.bid_currency} ${numeral(bid?.bid_proposed_max_price).format("0,0.00")}`;
                                                const service = String(bid?.bid_service_type || "").replaceAll("_", " ");
                                                const desc = (bid?.bid_summary && String(bid.bid_summary).trim()) || "—";

                                                return (
                                                    <div className="auction-card auction-card" key={bid?._id}>
                                                        <div className="auction-card-top">
                                                            <div className="auction-card-badges">
                                                                <span className="auction-pill auction-pill--muted">{bid?.bid_no || ""}</span>
                                                                <span className={`auction-pill auction-pill--priority ${urg.cls}`}>{urg.label}</span>
                                                                <span className="auction-pill auction-pill--tag">{service || "SERVICE"}</span>
                                                                <span className="auction-pill auction-pill--tag">{maxPrice}</span>
                                                            </div>

                                                            <div className={`auction-status ${trimString(bid?.bid_status).toLowerCase()}`}>
                                                                <span className={`auction-status-dot ${trimString(bid?.bid_status).toLowerCase()}`} />
                                                                <span className="auction-status-text">{st.label}</span>
                                                            </div>
                                                        </div>

                                                        <div className="auction-card-body">
                                                            <div className="auction-main">
                                                                <div className="auction-title">{bid?.bid_title || ""}</div>
                                                                <div className="auction-desc">{desc}</div>

                                                                <div className="auction-meta-user">
                                                                    <div className="auction-avatar small">
                                                                        {bid?.bid_principal?.usr_photo_url ? (
                                                                            <img src={bid?.bid_principal?.usr_photo_url} alt="" className="auction-avatar-img" />
                                                                        ) : (
                                                                            <span className="auction-avatar-initials">
                                                                                <InitialsAvatar
                                                                                    name={bid?.bid_principal?.usr_full_name || "System"}
                                                                                    size={32}
                                                                                    currentTheme={currentTheme}
                                                                                />
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <span className="auction-meta-text">
                                                                        {t('principal')}{" "}
                                                                        <b>{bid?.bid_principal?.usr_full_name || "System"}</b>
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="auction-side">
                                                                <div className="auction-sla">
                                                                    <div className="auction-sla-top">
                                                                        <span className="auction-sla-label">{t('sla').toUpperCase()}</span>
                                                                        <motion.span
                                                                            className={`auction-sla-remaining ${sla.cls}`}
                                                                            initial={sla.enabled && !sla.breached ? { scale: 0.96, opacity: 0.7 } : false}
                                                                            animate={sla.enabled && !sla.breached ? { scale: 1, opacity: 1 } : false}
                                                                            transition={{ duration: 0.12 }}
                                                                            title={sla.enabled ? "Vendor response SLA" : "Not started"}
                                                                        >
                                                                            {sla.remainingLabel}
                                                                        </motion.span>
                                                                    </div>

                                                                    <div className="auction-sla-bar">
                                                                        <div
                                                                            className={`auction-sla-fill ${sla.cls}`}
                                                                            style={{ width: `${Math.min(100, Math.max(0, sla.pct || 0))}%` }}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <button className="auction-link" onClick={() => navigate(`/bids/${bid?._id}`)}>
                                                                    {t('details')}
                                                                    <span className="auction-link-arrow">›</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {paged.length === 0 && (
                                                <div className="auctions-empty">
                                                    <div className="auctions-empty-title">{t("noAuctionsFound") || "No auctions found"}</div>
                                                    <div className="auctions-empty-sub">Try adjusting your filters.</div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="auctions-pagination">
                                            <div className="auctions-pagination-left">
                                                <span className="auctions-pagination-text">
                                                    {t("showing")}{" "}
                                                    <b>{effectiveRows.length === 0 ? 0 : page * rowsPerPage + 1}</b>
                                                    {" - "}
                                                    <b>{Math.min((page + 1) * rowsPerPage, effectiveRows.length)}</b> of{" "}
                                                    <b>{serverMode ? serverTotal : effectiveRows.length}</b>
                                                </span>
                                            </div>

                                            <TablePagination
                                                component="div"
                                                count={serverMode ? serverTotal : effectiveRows.length}
                                                page={page}
                                                onPageChange={(e, newPage) => setPage(newPage)}
                                                rowsPerPage={rowsPerPage}
                                                onRowsPerPageChange={(e) => {
                                                    setRowsPerPage(parseInt(e.target.value, 10));
                                                    setPage(0);
                                                }}
                                                rowsPerPageOptions={[4]}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="auctions-right-wrapper">stats content</div>
                    </div>
                </motion.div>
            )}

            <Dialog
                open={isNotifyDialogOpen}
                onClose={() => setIsNotifyDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" } } }}
            >
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: notifyDialogText }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton onClick={() => setIsNotifyDialogOpen(false)}>
                        {t("close")}
                    </CustomLoadingButton>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                onClose={() => setIsConfirmDialogOpen(false)}
                onConfirm={handleConfirm}
                dialogTitle={t("confirmation")}
                dialogContent={<p dangerouslySetInnerHTML={{ __html: confirmationActiontext }} />}
            />
        </div>
    );
};

export default AdAuctions;
